/**
 * PWA and Notification Service
 * Handles service worker, offline support, and push notifications
 */

import { getRuntimeConfig, isElectron } from '../config/runtime';
import { subscribeWebPush } from './pushNotifications';

/**
 * Initialize PWA features
 */
export async function initializePWA(): Promise<void> {
  // Skip PWA features in Electron (uses native notifications)
  if (isElectron()) {
    console.log('[PWA] Skipping PWA features in Electron app');
    return;
  }

  // Cloud mode: the app is served from the cloud's own origin (at `/app`),
  // where the CLOUD's service worker already owns the scope — and that worker
  // is the one that handles push. This used to unregister every worker it found
  // here, which tore down the cloud's own worker and, with it, any chance of
  // receiving a notification. Not registering one is the correct half of that
  // instinct; unregistering was the half that did the damage.
  try {
    const config = getRuntimeConfig();
    if (config.mode === 'cloud') {
      console.log("[PWA] Cloud mode — leaving this origin's service worker alone");
      return;
    }
  } catch {
    // Ignore runtime-config lookup failures and continue with default behavior.
  }

  // Skip service worker in Vite dev server to avoid MIME type issues
  // Service workers only work with production builds served over HTTP(S)
  if (import.meta.env.DEV) {
    console.log('[PWA] Skipping service worker in dev mode');
    return;
  }

  try {
    // Register service worker
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });
        console.log('[PWA] Service worker registered successfully:', registration);

        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60000); // Check every minute

        // Listen for controller change (new SW activated)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('[PWA] Service worker updated, app will reload on next navigation');
        });
      } catch (error) {
        console.warn('[PWA] Service worker registration failed:', error);
      }
    }

    // Deliberately no permission prompt here. Asking on every app start is the
    // pattern browsers penalise — with no user gesture behind it the prompt is
    // hidden or auto-denied, and a deny is remembered for the origin. The
    // account area asks properly, on a button (`subscribeWebPush` with
    // `askPermission`), and this path only subscribes on a permission already
    // granted.
    await registerDeviceForPush();
  } catch (error) {
    console.warn('[PWA] PWA initialization failed:', error);
  }
}

/**
 * Ask the cloud to reach this browser, when it already may.
 *
 * `subscribeWebPush` owns the whole question — whether this build can hold a
 * subscription at all, whether the deployment can deliver one, and whether the
 * person has said yes — and answers with a reason rather than a boolean, so the
 * log line says which door was shut instead of "push is broken".
 */
async function registerDeviceForPush(): Promise<void> {
  try {
    const outcome = await subscribeWebPush();
    if (outcome.ok) return;

    if (outcome.reason === 'permission-denied') {
      console.info('[PWA] No notification permission yet — the account area can ask for it');
      return;
    }
    console.info(`[PWA] Web Push not subscribed (${outcome.reason})`);
  } catch (error) {
    console.warn('[PWA] Failed to register this browser for push:', error);
  }
}

/**
 * Request update check (for apps with update notifications)
 */
export function checkForUpdates(): void {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
  }
}
